package com.techspiration.webuserapp.api

import com.techspiration.core.contract.ApiRoutes
import com.techspiration.core.contract.ContractValidation
import com.techspiration.core.contract.WebEventRequest
import com.techspiration.core.contract.WebEventType
import com.techspiration.core.contract.WebStoreTarget
import com.techspiration.core.contract.contractJson
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlin.js.json
import kotlin.random.Random
import org.w3c.fetch.RequestInit

/**
 * Tanıtım sitesi telemetrisi — sayfa ziyareti ve mağaza-linki tıklaması.
 *
 * **Ateşle-unut.** Her iki fonksiyon da anında döner; gönderim ayrı bir coroutine'de yürür.
 * [trackStoreClick] tıklamayı ASLA `preventDefault` etmez ve beklemez → ağ yavaş olsa bile
 * kullanıcı mağazaya gitmekte gecikmez. Telemetri hatası sayfayı etkilemez: yutulur, konsola
 * tek satır düşer.
 *
 * **Gizlilik.** Çerez yoktur. `sessionId` sekme ömürlü rastgele bir değerdir
 * (`sessionStorage`) — kullanıcıyı ziyaretler arasında izlemez, kişi tanımlayıcı değildir;
 * yalnız sunucunun "aynı ziyaret" tekilleştirmesi için vardır. `referrer` gönderilmeden önce
 * sorgu ve parça (`?`/`#`) kısmı atılır ki izleme parametreleri (utm_*, fbclid…) backend'e sızmasın.
 * IP hiç gönderilmez; sunucu onu tuzlayıp hash'ler.
 *
 * Ktor client değil tarayıcının `fetch`'i kullanılır — gerekçe [PublicApi] KDoc'unda (bundle boyutu).
 */
object WebTelemetry {

    private val scope = MainScope()
    private val api = TelemetryApi()

    /**
     * Sayfa açılışında bir kez `page_view` gönderir.
     *
     * **Çift sayma neden imkânsız:** bu fonksiyon `main()`'den, yani Compose kompozisyonunun
     * DIŞINDAN çağrılır. `main()` belge yüklenmesi başına bir kez koşar; recomposition,
     * `LaunchedEffect` yeniden çalışması veya rota değişimi buraya ulaşamaz — SPA içi gezinme
     * (`UserNavigator`) yeni bir `page_view` üretmez, çünkü tek bir belge yüklemesi tek ziyarettir.
     * [sent] bayrağı ikinci bir savunma hattıdır: birileri ileride bunu bir composable'dan
     * çağırırsa yine tek olay gider.
     */
    fun trackPageView() {
        if (sent) return
        sent = true
        send(
            WebEventRequest(
                eventType = WebEventType.PAGE_VIEW,
                sessionId = sessionId(),
                referrer = referrer(),
            ),
        )
    }

    /** Mağaza linki tıklaması. Çağrı anında döner; navigasyon engellenmez/geciktirilmez. */
    fun trackStoreClick(target: WebStoreTarget) {
        send(
            WebEventRequest(
                eventType = WebEventType.STORE_CLICK,
                target = target,
                sessionId = sessionId(),
                referrer = referrer(),
            ),
        )
    }

    private var sent = false

    private fun send(request: WebEventRequest) {
        scope.launch {
            runCatching { api.report(request) }
                .onFailure { console.warn("Telemetri gönderilemedi: ${it.message}") }
        }
    }

    /**
     * Sekme ömürlü rastgele oturum kimliği. `sessionStorage` gizlilik modlarında erişimde
     * fırlatabildiği için hata yutulur → kimlik olmadan da olay gönderilir (yalnız o ziyaret
     * "benzersiz oturum" sayımına girmez).
     */
    private fun sessionId(): String? = runCatching {
        val storage = window.sessionStorage
        storage.getItem(SESSION_KEY) ?: randomId().also { storage.setItem(SESSION_KEY, it) }
    }.getOrNull()

    /** 16 haneli hex — çakışma olasılığı bu ölçekte yok denecek kadar düşük, PII taşımaz. */
    private fun randomId(): String = (0 until SESSION_ID_HEX_CHARS)
        .joinToString("") { Random.nextInt(HEX_RADIX).toString(HEX_RADIX) }

    /** Yönlendiren sayfa; sorgu/parça atılır ve sunucu sınırına kırpılır. */
    private fun referrer(): String? = document.referrer
        .takeIf { it.isNotBlank() }
        ?.substringBefore('#')
        ?.substringBefore('?')
        ?.take(ContractValidation.WEB_REFERRER_MAX)

    private const val SESSION_KEY = "kamuapp-telemetry-session"
    private const val SESSION_ID_HEX_CHARS = 16
    private const val HEX_RADIX = 16
}

/**
 * Telemetri ucunun ham HTTP katmanı — [PublicApi]'den AYRI tutuldu: o okur ve yanıtı ekrana
 * taşır, bu yazar ve yanıtı umursamaz (sunucu 204 döner).
 */
internal class TelemetryApi(
    private val baseUrl: String = defaultBaseUrl,
) {
    /**
     * Olayı gönderir. `keepalive = true`: kullanıcı isteği tamamlanmadan sekmeyi kapatsa/başka
     * sayfaya geçse bile tarayıcı gönderimi tamamlar — mağaza tıklaması gibi "gitmeden hemen önce"
     * üretilen olaylar kaybolmasın.
     */
    suspend fun report(request: WebEventRequest) {
        val response = window.fetch(
            "$baseUrl${ApiRoutes.PUBLIC_WEB_EVENTS}",
            RequestInit(
                method = "POST",
                headers = json("Content-Type" to "application/json"),
                body = contractJson.encodeToString(WebEventRequest.serializer(), request),
                keepalive = true,
            ),
        ).await()
        check(response.ok) { "HTTP ${response.status}" }
    }
}
